import React from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import classNames from "classnames";

import { Button } from "../../../components/Button/Button";
import { Collapse } from "../../../components/Collapse/Collapse";
import { intensityRangeText } from "../../../utils/intensityScale";
import {
  PROFILE_AXES,
  ProfileAxis,
  ProfileRange,
  StyleProfile,
} from "../../ShowStylePage/profile";
import { ProfileRadar } from "./ProfileRadar";
import { ProfileRangeSlider } from "./ProfileRangeSlider";

interface ProfileSectionProps {
  isLocked: boolean;
  isOpen: boolean;
  onToggle: () => void;
  isGuidelinesGenerated: boolean;
}

const DEFAULT_RANGE: ProfileRange = { min: 3, max: 3 };

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  isLocked,
  isOpen,
  onToggle,
  isGuidelinesGenerated,
}) => {
  const { t, i18n } = useTranslation();
  const { watch, setValue } = useFormContext();

  const profile = (watch("profile") || {}) as StyleProfile;

  const labels: Record<ProfileAxis, string> = {
    bitterness: t("Pages:StyleCreator.Profile.Bitterness", "Bitterness"),
    hoppiness: t("Pages:StyleCreator.Profile.Hoppiness", "Hoppiness"),
    maltiness: t("Pages:StyleCreator.Profile.Maltiness", "Maltiness"),
    body: t("Pages:StyleCreator.Profile.Body", "Body"),
    esters: t("Pages:StyleCreator.Profile.Esters", "Esters"),
    sourness: t("Pages:StyleCreator.Profile.Sourness", "Sourness"),
    smokiness: t("Pages:StyleCreator.Profile.Smokiness", "Smokiness"),
  };

  const rangeText = (range: ProfileRange) =>
    intensityRangeText(t, range.min, range.max, i18n?.language);

  const setRange = (axis: ProfileAxis, range: ProfileRange | null) =>
    setValue("profile", { ...profile, [axis]: range }, { shouldDirty: true });

  return (
    <Collapse
      title={
        <div className="flex justify-between items-center w-full min-w-0 pr-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              {isLocked && <span className="icon-[ph--lock] text-[#877369] text-base" />}
              <span
                className={classNames(
                  "text-lg font-bold font-header",
                  isLocked ? "text-[#877369]" : "text-[#6c2f00]",
                )}
              >
                {t("Pages:StyleCreator.Profile.Title", "5. Style profile")}{" "}
                <span className="font-normal text-xs text-[#54433a] font-sans">
                  ({t("Pages:StyleCreator.Profile.SensoryBalance", "sensory balance")})
                </span>
              </span>
            </div>
            {!isLocked && (
              <span className="text-xs text-[#54433a] font-normal font-sans">
                {t(
                  "Pages:StyleCreator.Profile.Subtitle",
                  "Shown to judges and participants on the style page as a quick guide. The written description stays authoritative.",
                )}
              </span>
            )}
          </div>
          {isGuidelinesGenerated && (
            <span className="px-3 py-1 bg-[#b1f0ce] text-[#0e5138] text-xs font-semibold rounded-full flex items-center gap-1">
              <span className="icon-[ph--check-circle] text-xs" />
              {t("Pages:StyleCreator.Profile.AutoGenerated", "Auto-generated")}
            </span>
          )}
        </div>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      variant="plain"
      density="comfortable"
      className={classNames(
        "rounded-xl border border-solid transition-all duration-200",
        isLocked ? "opacity-60 cursor-not-allowed" : "",
      )}
      headerClassName={isLocked ? "pointer-events-none" : ""}
    >
      <fieldset disabled={isLocked} className="w-full space-y-6">
        <p className="text-xs text-[#54433a]">
          {t(
            "Pages:StyleCreator.Profile.Help",
            "Set the lowest and the highest level the style allows for each trait. One dot means a single level; \"None\" means the trait does not occur. A trait you leave unset is not shown.",
          )}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 w-full">
          <div className="space-y-4">
            {PROFILE_AXES.map((axis) => {
              const range = profile[axis];
              return (
                <div key={axis} className="space-y-1" data-testid={`profile-row-${axis}`}>
                  <div className="flex justify-between items-center gap-2 text-xs font-bold uppercase text-[#877369] tracking-wider">
                    <span>{labels[axis]}</span>
                    <span className="flex items-center gap-2">
                      <span data-testid={`profile-value-${axis}`}>
                        {range
                          ? rangeText(range)
                          : t("Pages:StyleCreator.Profile.NotSet", "Not set")}
                      </span>
                      <Button
                        htmlType="button"
                        type="base"
                        variant="ghost"
                        onClick={() => setRange(axis, range ? null : DEFAULT_RANGE)}
                        className="p-0 text-[10px] font-bold normal-case text-[#6c2f00] hover:underline"
                      >
                        {range
                          ? t("Pages:StyleCreator.Profile.Clear", "Clear")
                          : t("Pages:StyleCreator.Profile.SetRange", "Set range")}
                      </Button>
                    </span>
                  </div>
                  {range ? (
                    <ProfileRangeSlider
                      label={labels[axis]}
                      value={range}
                      onChange={(next) => setRange(axis, next)}
                      minLabel={t("Pages:StyleCreator.Profile.From", "from")}
                      maxLabel={t("Pages:StyleCreator.Profile.To", "to")}
                      disabled={isLocked}
                    />
                  ) : (
                    <div className="h-6" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center bg-[#fff1e9] rounded-xl p-4 border border-[#e5e0d8] shadow-inner">
            <div className="w-full max-w-sm aspect-[124/108]">
              <ProfileRadar
                profile={profile}
                labels={labels}
                ariaLabel={t("Pages:StyleCreator.Profile.RadarLabel", "Style profile radar")}
              />
            </div>
          </div>
        </div>
      </fieldset>
    </Collapse>
  );
};
